import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DealReplaceConfirmModal from '../components/DealReplaceConfirmModal';
import Logo from '../components/Logo';
import { getDealReplaceApiUrl } from '../utils/apiUrls';
import { dealReplaceFileBlocks, namesLine } from '../utils/dealReplaceConfirm';
import '../components/DealReplaceConfirmModal.css';

const AUTO_RESOLVE_ACTIONS = new Set(['draft_no']);

const DealReplaceMagicLinkLanding = () => {
  const { token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const action = new URLSearchParams(location.search).get('action') || '';
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [dismissed, setDismissed] = useState(false);
  const [doneMessage, setDoneMessage] = useState('');
  const autoResolve = AUTO_RESOLVE_ACTIONS.has(action);
  const listingDraftYes = action === 'draft_yes'
    && Boolean(preview)
    && !preview.existing_contract_id;

  useEffect(() => {
    if (!token) {
      return undefined;
    }
    let cancelled = false;
    axios.get(`${getDealReplaceApiUrl()}/${token}/preview`)
      .then((response) => {
        if (!cancelled) {
          setPreview(response.data);
        }
      })
      .catch((err) => {
        if (!cancelled && !autoResolve) {
          setError(err.response?.data?.message || 'This confirmation link is no longer valid.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [autoResolve, token]);

  useEffect(() => {
    if (action !== 'draft_yes' || !preview || !token) {
      return undefined;
    }
    if (preview.existing_contract_id) {
      navigate(
        `/contract/${preview.existing_contract_id}?dealReplaceToken=${encodeURIComponent(token)}&dealReplaceAction=draft_yes&createForm=notice_of_cancellation`,
        { replace: true },
      );
      return undefined;
    }
    let cancelled = false;
    axios.post(
      `${getDealReplaceApiUrl()}/${token}/resolve`,
      { action: 'draft_yes' },
      { headers: { Accept: 'application/json' } },
    )
      .then((response) => {
        if (!cancelled) {
          setDoneMessage(response.data?.message || 'Done.');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Could not complete that action.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [action, navigate, preview, token]);

  useEffect(() => {
    if (!token || !autoResolve) {
      return undefined;
    }
    let cancelled = false;
    axios.post(
      `${getDealReplaceApiUrl()}/${token}/resolve`,
      { action },
      { headers: { Accept: 'application/json' } },
    )
      .then((response) => {
        if (!cancelled) {
          setDoneMessage(response.data?.message || 'Done.');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Could not complete that action.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [action, autoResolve, token]);

  const kind = (preview?.held_doc_type || '').startsWith('listing') ? 'listing' : 'contract';
  const blocks = dealReplaceFileBlocks(preview, kind);
  const existing = blocks[0];

  if (!action || (error && !autoResolve)) {
    return (
      <div className="deal-replace-magic-page">
        <header className="deal-replace-magic-header">
          <Logo variant="arrow" />
          <h1>KeyFlo</h1>
        </header>
        <div className="deal-replace-magic-card">
          <h2>Link unavailable</h2>
          <p>{error || 'This confirmation link is missing an action.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="deal-replace-magic-page">
      <header className="deal-replace-magic-header">
        <Logo variant="arrow" />
        <div>
          <h1>{existing?.address || preview?.incoming_address_display || 'Property confirmation'}</h1>
          <p>
            {kind === 'contract'
              ? `${namesLine(preview?.existing_sellers)} → ${namesLine(preview?.existing_buyers)}`
              : `Sellers — ${namesLine(preview?.existing_sellers)}`}
          </p>
        </div>
      </header>
      {autoResolve && (
        <div className="deal-replace-magic-card">
          <h2>
            {kind === 'listing' ? 'No listing withdrawal' : 'No draft needed'}
          </h2>
          <p>
            {error
              || doneMessage
              || 'Recording that no draft is needed…'}
          </p>
        </div>
      )}
      {action === 'draft_yes' && (
        <div className="deal-replace-magic-card">
          <h2>{kind === 'listing' ? 'Listing withdrawal' : 'Cancellation form'}</h2>
          <p>
            {error
              || doneMessage
              || (listingDraftYes
                ? 'Recording the listing withdrawal request…'
                : 'Opening the Notice of Cancellation…')}
          </p>
        </div>
      )}
      {!autoResolve && action !== 'draft_yes' && !dismissed && (
        <DealReplaceConfirmModal
          token={token}
          action={action}
          onClose={(message) => {
            setDoneMessage(message || '');
            setDismissed(true);
          }}
        />
      )}
      {!autoResolve && dismissed && (
        <div className="deal-replace-magic-card">
          <p>
            {doneMessage
              || 'No change was made. Reopen the email button if you still need to confirm.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default DealReplaceMagicLinkLanding;
